import { html, css } from "lit";
import { customElement, query, queryAll } from "lit/decorators.js";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { msg } from "@omegagrid/localize";
import { map } from "lit/directives/map.js";
import { ContextFilter, ContextFilterItem } from "@reporting/rp-types/analytics";
import { DimTreeAdapter } from "../../adapters/dimAdapter";
import { DimensionDefinition } from "@reporting/rp-types/definition";
import { Button, dom } from "@omegagrid/core";
import { TreeEvent, TreeMenu, TreeSource, TreeDropdown, SerializedValue } from "@omegagrid/tree";
import { BaseSection } from "./sectionBase";
import { createRef, ref } from "lit/directives/ref.js";
import { isStandardDimension, getContextYears } from "../../common";
import { DimAttributeTreeAdapter } from "../../adapters";

@customElement('rp-fsection-dimensions')
export class DimensionSection extends BaseSection {

	static styles = [...BaseSection.styles, css`
		og-button.add {
			border: 1px dashed var(--og-accent-color);
			width: 100%;
		}

		.row {
			display: flex;
			align-items: center;
		}

		.row og-tree-dropdown {
			flex: 1;
			border: 1px solid var(--og-colortype-accent-border-color);
			background-color: var(--og-accent-color-alpha-30);
			color: var(--og-text-color-3);
		}

		.row og-button {
			flex: 0;
		}
	`];

	@query('og-button.add')
	addButton!: Button;

	@queryAll('og-button.remove')
	removeButtons!: NodeListOf<Button>;

	@query('.dropdown-invoice')
	dropdownInvoice!: TreeDropdown;

	private addMenuRef = createRef<TreeMenu>();
	get addMenu() { return this.addMenuRef?.value }

	private _filters: ContextFilter[];
	get filters() {
		if (this._filters) return this._filters;
		if (!this.context) return [];
		this._filters = this.context.filters?.filter(f => isStandardDimension(f.dim)) as ContextFilter[] ?? [];
		return this._filters;
	}

	private adapters = new Map<string, DimTreeAdapter>;
	getAdapter(dimNum: string) {
		if (!this.adapters.has(dimNum) && this.service && this.module) {
			this.adapters.set(dimNum, new DimTreeAdapter(this.module.module as ModuleCode, dimNum));
		}

		const adapter = this.adapters.get(dimNum);
		adapter.years = getContextYears(this.context, {monthOffset: 0}); // TODO: use correct fiscal year definition
		return adapter;
	}

	resetItems() {
		this.adapters.clear();
		this.requestUpdate();
	}

	willUpdate(props: Map<PropertyKey, unknown>) {
		super.willUpdate(props);
		if (props.has('analyticsClient') || props.has('module')) {
			if (this.service && this.module) {
				this.adapters.clear();
			}
		}
		this._filters = null;
	}

	get addMenuItems() {
		const items: TreeSource = [];
		this.module.dimensions.forEach((dim, dimIndex) => {
			items.push({
				key: dimIndex.toString(),
				value: dim.description,
				icon: 'hexagon',
			});
			dim.properties?.forEach((prop, i) => items.push({
				key: `${dimIndex}_${i}`,
				value: prop.name,
				level: 1,
				icon: 'brackets-curly',
			}));
		});
		return items;
	}

	getInvoiceFilter() {
		return this.context?.filters?.find(f => f.dim == 'IR');
	}

	updated() {
		this.addMenu?.setSourceData(this.addMenuItems, {
			selectMode: 'single',
			checkboxes: false
		});

		if (this.dropdownInvoice) {
			this.dropdownInvoice.setSourceData([
				{k: 'I', v: msg('Receivable') + ' (AR)'},
				{k: 'R', v: msg('Payable') + ' (AP)'},
			], {selectMode: 'multiple'});

			const invoiceFilter = this.getInvoiceFilter();
			this.dropdownInvoice.value = invoiceFilter?.items as SerializedValue[] || [];
		}
	}

	disconnectedCallback(): void {
		this.addMenu?.remove();
	}

	_onFilterChange = (e: TreeEvent, index: number, dim: DimensionDefinition) => {
		let filter = this.filters[index];
		if (!filter) filter = {dim: dim.dim_num, items: null};
		filter.items = e.component.selection.serialize() as undefined as ContextFilterItem[];
		if (filter.items.length == 0) dom.showElement(this.removeButtons[index]);
		else dom.hideElement(this.removeButtons[index]);

		this.updateFilters();
	}

	openAddMenu() {
		dom.setSize(this.addMenu, {w: this.addButton.offsetWidth});
		this.addMenu.open(this.addButton);
	}

	updateFilters(refresh?: boolean) {
		this.updateContext('filters', [
			...(this.context.filters || []).filter(f => !isStandardDimension(f.dim)) as ContextFilter[],
			...this.filters
		]);
		if (refresh) this.requestUpdate();
	}

	addFilter(key: string) {
		const parts = key.split('_');
		const dimIndex = parseInt(parts[0]);
		const attrIndex = parts[1] ? parseInt(parts[1]) : -1;
		let dim = this.module.dimensions[dimIndex];

		let dimNum = dim.dim_num as string;
		if (attrIndex > -1) dimNum = `${dimNum}:${dim.properties[attrIndex].key}`;

		this.filters.push({
			dim: dimNum,
			items: []
		});

		this.updateFilters(true);
	}

	removeFilter(index: number) {
		this.filters.splice(index, 1);
		this.updateFilters(true);
	}

	_onInvoiceTypeChange = (e: TreeEvent) => {
		//this.context.filters = this.context.filters?.filter(f => !['IN', 'IR'].includes(f.dim));
		let filter = this.getInvoiceFilter();

		if (!filter) {
			filter = {dim: 'IR', items: []};
			this.context.filters = [...(this.context.filters || []), filter];
		}

		filter.items = this.dropdownInvoice.selection.serialize() as ContextFilterItem[];
		this.updateContext('filters', this.context.filters);
	}

	render = () => html`
		${this.module.module == 'A' ? html`
			<div class="row">
				<og-tree-dropdown
					.caption="${msg('Type')}"
					class="dropdown-invoice"
					color="transparent"
					.adapter="${this.getAdapter('IR')}"
					@change="${this._onInvoiceTypeChange}">
				</og-tree-dropdown>
			</div>
		` : '' }

		${map(this.filters, (filter, index) => {
			const parts = filter.dim.split(':', 2);
			const dimNum = parts[0];
			const attribute = parts[1];
			return html`
				<div class="row" data-index="${index}">
					<og-tree-dropdown
						color="transparent"
						.caption="${attribute
							? `${attribute} (${this.dimensionMap.get(dimNum)?.description})`
							: this.dimensionMap.get(dimNum)?.description
						}"
						.adapter="${attribute
							? new DimAttributeTreeAdapter(this.module.module as ModuleCode, dimNum, attribute)
							: this.getAdapter(dimNum)
						}"
						.value="${filter.items}"
						@change="${(e: TreeEvent) => this._onFilterChange(e, index, this.dimensionMap.get(dimNum))}"
						cross>
					</og-tree-dropdown>

					<og-button
						class="remove"
						style="flex: 0 0 20px; display: ${filter?.items?.length ? 'none' : 'block'}"
						.icon="${{icon: 'trash', color: 'red'}}"
						color="tranparent"
						@click="${() => this.removeFilter(index)}">
					</og-button>
				</div>
			`;
		})}

		${this.module?.dimensions ? html`
			<div class="row" style="display: block; margin-bottom: 0">
				<og-button
					caret="down"
					class="add"
					color="transparent"
					@click="${() => this.openAddMenu()}">
					<div slot="content">+ ${msg('Add dimension filter')}</div>
				</og-button>
			</div>
		` : null}

		<og-tree-menu ${ref(this.addMenuRef)}
			color="transparent"
			style="display: none; z-index: 1000"
			maxHeight="200"
			@select="${(e: TreeEvent) => this.addFilter(e.node.key as string)}">
		</og-tree-menu>
	`;

}
